/**
 * Python scripts executed on the device via MpRawMode.exec().
 *
 * These are the single source of truth for all device-side Python code.
 * Shared between the connection actor (initial setup) and fileOps.ts (on-demand operations).
 */

/**
 * Get device info: machine name, release version, sysname, language.
 * Output: pipe-delimited string "machine|release|sysname|language"
 */
export const DEVICE_INFO_SCRIPT = `import sys,os
try: u=os.uname()
except: u=('','','','',sys.platform)
try:
 lang='CircuitPython' if 'CircuitPython' in sys.version else 'MicroPython'
except:
 lang='MicroPython'
print(u[4]+'|'+u[2]+'|'+u[0]+'|'+lang)
`;

/**
 * Walk the filesystem and print a line-delimited listing.
 * Output format: "f|path|size" for files, "d|path/|0" for directories.
 * Paths are relative to root (no leading /).
 * @param root - Root path to walk from (empty string for full filesystem)
 */
export function getFilesystemScript(root = ''): string {
    const walkArg = root ? `'${root}'` : "''";
    return `import os
def walk(p):
 for n in os.listdir(p if p else '/'):
  fn=(p+'/'+n) if p else n
  try: s=os.stat(fn)
  except: s=(0,)*7
  if s[0] & 0x4000:
   print('d|'+fn+'/|0')
   walk(fn)
  else:
   print('f|'+fn+'|'+str(s[6]))
walk(${walkArg})
`;
}

/**
 * Check if CircuitPython filesystem is read-only.
 * Output: "True" or "False"
 */
export const READ_ONLY_CHECK_SCRIPT = `import storage;print(storage.getmount('/').readonly)`;

/**
 * Read a file as base64-encoded chunks (192 bytes → 256 base64 chars per line).
 * @param path - Escaped file path
 */
export function getReadFileScript(path: string): string {
    return `import sys,binascii
with open('${path}','rb') as f:
 while 1:
  b=f.read(192)
  if not b:break
  sys.stdout.write(binascii.b2a_base64(b).decode())
`;
}

/**
 * Ensure parent directories exist for a given path.
 * @param dirPath - Escaped directory path
 */
export function getEnsureDirScript(dirPath: string): string {
    return `import os
def ensure_dir(p):
 parts=p.split('/')
 for i in range(1,len(parts)+1):
  d='/'.join(parts[:i])
  if d:
   try: os.mkdir(d)
   except OSError: pass
ensure_dir('${dirPath}')
`;
}

/**
 * Create a directory (with parents).
 * @param dirname - Escaped directory path
 */
export function getCreateDirScript(dirname: string): string {
    return `import os
def ensure_dir(p):
 parts=p.rstrip('/').split('/')
 for i in range(1,len(parts)+1):
  d='/'.join(parts[:i])
  if d:
   try: os.mkdir(d)
   except OSError as e:
    if e.args[0]!=17: raise
ensure_dir('${dirname}')
`;
}

/**
 * Recursively delete a directory.
 * @param path - Escaped directory path
 */
export function getDeleteTreeScript(path: string): string {
    return `import os
def rmtree(p):
 for entry in os.ilistdir(p):
  name=entry[0]
  is_dir=entry[1]==0x4000
  full=p+'/'+name
  if is_dir:
   rmtree(full)
  else:
   os.remove(full)
 os.rmdir(p)
rmtree('${path}')
`;
}

// --- File Hash Scripts ---

// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_RE = /[\x00-\x1f\x7f]/;

/**
 * Escape a file path for safe embedding in a Python string literal.
 * Escapes backslashes and single quotes to prevent injection.
 * Throws if the path contains control characters that could break the Python literal.
 */
function escapePathForPython(path: string): string {
    if (CONTROL_CHAR_RE.test(path)) {
        throw new Error(
            'File path cannot contain control characters (bytes 0x00–0x1F, 0x7F)',
        );
    }
    return path.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

/**
 * Generate a Python script that computes the SHA-256 hash of a single file on device.
 * Output: 64-char lowercase hex hash string printed to stdout.
 * @param path - Absolute device file path
 */
export function generateSingleFileHashScript(path: string): string {
    const escaped = escapePathForPython(path);
    return `import hashlib,binascii
def file_hash(p):
 h=hashlib.sha256()
 with open(p,'rb') as f:
  while 1:
   c=f.read(4096)
   if not c:break
   h.update(c)
 return binascii.hexlify(h.digest()).decode()
print(file_hash('${escaped}'))
`;
}

/**
 * Generate a Python script that computes SHA-256 hashes for multiple files on device.
 * Output: JSON object mapping path → hash (or null if file inaccessible).
 * @param paths - Array of absolute device file paths
 */
export function generateBatchFileHashScript(paths: string[]): string {
    const escapedPaths = paths.map((p) => `'${escapePathForPython(p)}'`);
    return `import hashlib,binascii,json
def file_hash(p):
 try:
  h=hashlib.sha256()
  with open(p,'rb') as f:
   while 1:
    c=f.read(4096)
    if not c:break
    h.update(c)
  return binascii.hexlify(h.digest()).decode()
 except OSError:
  return None
paths=[${escapedPaths.join(',')}]
result={}
for p in paths:
 result[p]=file_hash(p)
print(json.dumps(result))
`;
}

// --- Hash Response Parsing ---

/** Regex matching a valid 64-char lowercase hex SHA-256 hash. */
const SHA256_HEX_RE = /^[0-9a-f]{64}$/;

/**
 * Parse the raw REPL output from a single-file hash script.
 * Extracts the 64-character hex hash from the output, ignoring any
 * surrounding whitespace or REPL noise.
 * @returns The hash string, or null if output is invalid.
 */
export function parseSingleHashResponse(output: string): string | null {
    const lines = output.trim().split('\n');
    for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i].trim();
        if (SHA256_HEX_RE.test(line)) {
            return line;
        }
    }
    return null;
}

/**
 * Parse the raw REPL output from a batch file hash script.
 * Extracts the JSON object mapping file paths to hash strings (or null for failures).
 * @returns A record of path → hash (null for inaccessible files), or empty object on parse failure.
 */
export function parseBatchHashResponse(
    output: string,
): Record<string, string | null> {
    const lines = output.trim().split('\n');
    // Find the last line that looks like JSON (starts with '{')
    for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i].trim();
        if (line.startsWith('{')) {
            try {
                const parsed: unknown = JSON.parse(line);
                if (
                    typeof parsed === 'object' &&
                    parsed !== null &&
                    !Array.isArray(parsed)
                ) {
                    const result: Record<string, string | null> = {};
                    for (const [key, value] of Object.entries(
                        parsed as Record<string, unknown>,
                    )) {
                        if (value === null) {
                            result[key] = null;
                        } else if (
                            typeof value === 'string' &&
                            SHA256_HEX_RE.test(value)
                        ) {
                            result[key] = value;
                        } else {
                            // Invalid hash value — treat as null
                            result[key] = null;
                        }
                    }
                    return result;
                }
            } catch {
                // Not valid JSON, continue searching
            }
        }
    }
    return {};
}
